import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { FeatureFlags } from "../services/api";
import { getFeatureFlags } from "../services/api";
import { useAuth } from "../auth/AuthContext";

// Default flags (all enabled) — used before flags are loaded or on fetch failure
const DEFAULT_FLAGS: FeatureFlags = {
  dashboard: true,
  myDashboard: true,
  jobBoard: true,
  reports: true,
  admin: true,
  people: true,
  beach: true,
  awsRrs: true,
  skills: true,
  assignments: true,
  opportunities: true,
  rrMatcher: true,
};

const FLAGS_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface FeatureFlagsContextValue {
  flags: FeatureFlags;
  loading: boolean;
  refresh: () => Promise<void>;
  isEnabled: (feature: keyof FeatureFlags) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export function FeatureFlagsProvider({ children }: FeatureFlagsProviderProps) {
  const { user } = useAuth();
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(false);

  async function loadFlags() {
    setLoading(true);
    try {
      const fetchedFlags = await getFeatureFlags();
      setFlags(fetchedFlags);
    } catch (error) {
      console.error("Failed to load feature flags, using defaults:", error);
      setFlags(DEFAULT_FLAGS);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user) {
      setFlags(DEFAULT_FLAGS);
      return;
    }

    void loadFlags();

    // Refresh when the tab regains focus — picks up changes made in another tab/session
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void loadFlags();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Periodic background refresh
    const interval = setInterval(() => void loadFlags(), FLAGS_POLL_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, [user]);

  const isEnabled = (feature: keyof FeatureFlags): boolean => flags[feature] === true;

  const value: FeatureFlagsContextValue = {
    flags,
    loading,
    refresh: loadFlags,
    isEnabled,
  };

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagsProvider");
  }
  return context;
}
